import { GlobalToken, theme } from "antd";
import { Menu, X } from "lucide-react";
import { FC, ReactNode, useEffect, useRef } from "react";
import { styled } from "styled-components";
import { AgentState } from "../../models/agent";
import AppBaseLayout from "./AppBaseLayout";
import CenterContent from "./CenterContent";

interface IProps {
  displayMenuButton?: boolean;
  mainContent: ReactNode;
  onBusinessUnitsPage?: boolean;
  menuContent?: ReactNode;
  agentState?: AgentState;
  isMenuOpen?: boolean;
  onMenuOpenChange?(isOpen: boolean): void;
}

const LayoutWithNav: FC<IProps> = ({ menuContent, mainContent, onBusinessUnitsPage = false, agentState, displayMenuButton = true, isMenuOpen, onMenuOpenChange }) => {
  const { token } = theme.useToken();

  const sideMenuContainerRef = useRef<HTMLDivElement>(null);

  useEffect(
    () => {
      const handleTouchStart = (event: TouchEvent) => {
        if (sideMenuContainerRef.current && !sideMenuContainerRef.current.contains(event.target as Node))
          onMenuOpenChange?.(false);
      };

      document.addEventListener("touchstart", handleTouchStart, true);
      return () => document.removeEventListener("touchstart", handleTouchStart, true);
    },
    [onMenuOpenChange],
  );

  return (
    <AppBaseLayout
      appContent={
        <AppContainer $onBusinessUnitsPage={onBusinessUnitsPage} $agentState={agentState}>
          {
            displayMenuButton && onMenuOpenChange &&
            <MenuButtonContainer $isMenuOpen={false}>
              <Menu size={token.fontSizeHeading2} onClick={() => onMenuOpenChange(true)} />
            </MenuButtonContainer>
          }
          <CenterContent>
            {mainContent}
          </CenterContent>
          {
            isMenuOpen && onMenuOpenChange &&
            <SideMenuContainer ref={sideMenuContainerRef}>
              <MenuButtonContainer $isMenuOpen={true}>
                <X size={token.fontSizeHeading2} onClick={() => onMenuOpenChange(false)} />
              </MenuButtonContainer>
              {menuContent}
            </SideMenuContainer>
          }
        </AppContainer>
      }
    />
  );
};

export default LayoutWithNav;

const AppContainer = styled.div<{ $onBusinessUnitsPage?: boolean; $agentState?: AgentState }>`
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background-color: ${p => getBackgroundColor(p.theme, p.$onBusinessUnitsPage, p.$agentState) ?? p.theme.colorBgContainer};
`;

const MenuButtonContainer = styled.div<{ $isMenuOpen: boolean }>`
  width: 100%;
  display: flex;
  justify-content: ${p => p.$isMenuOpen ? "flex-end" : "flex-start"};
  align-items: center;
  padding: ${p => p.theme.padding}px ${p => p.theme.padding}px ${p => p.theme.paddingXXS}px ${p => p.theme.paddingLG}px; 
  background-color: inherit;
`;

const SideMenuContainer = styled.div`
  position: absolute;
  z-index: 9999;
  top: 0;
  left: 0;
  bottom: 0;
  width: 65%;
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.paddingXL}px;
  background-color: ${p => p.theme.colorBgLayout};
  
  transform: translateX(-100%);
  animation: slideIn 0.12s cubic-bezier(0.3, 0, 0.2, 1) forwards;

  @keyframes slideIn {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0%);
    }
  }
`;

function getBackgroundColor(theme: GlobalToken, onBusinessUnitsPage?: boolean, state?: AgentState) {
  if (onBusinessUnitsPage) {
    return theme.colorPrimaryBg;
  }

  if (state) {
    switch (state) {
      case "Idle":
        return theme.colorSuccess;
      case "Break":
        return theme.colorWarning;
      case "Busy":
        return theme.colorPrimary;
      case "LoggedOut":
      case "PendingBreak":
      case "PendingLogout":
      default:
        return theme.colorPrimaryBg;
    }
  }
}
